import { useEffect, useState } from "react";
import { Link, NavLink, Routes, Route, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { Menu, X, ArrowRight, GitMerge, MessagesSquare, FlaskConical, Presentation, GraduationCap, Crosshair, Target, CheckCircle2, Link2, ShieldCheck, Users, Quote, Sparkles, Pill, Stethoscope, Microscope, Globe2, UserRound, ListOrdered, Dna, Scale, HelpCircle } from "lucide-react";
import { NodeCanvas, Reveal, Counter } from "./SiteVisuals";

const META = {
  "/": ["HCP Voice | Evidence-Calibrated HCP Intelligence", "HCP Voice transforms healthcare-professional conversations and scientific evidence into calibrated, traceable and actionable intelligence for life-sciences teams."],
  "/platform": ["Platform | HCP Voice", "Healthcare-professional intelligence, scientifically calibrated — six connected intelligence capabilities beyond conventional sentiment analysis."],
  "/how-it-works": ["How It Works | HCP Voice", "A transparent, expert-reviewed four-step process: define, gather, calibrate and translate — from fragmented evidence to a coherent clinical voice."],
  "/applications": ["Applications | HCP Voice", "Designed for decisions across the product lifecycle — Medical Affairs, clinical development, market access, competitive intelligence and leadership."],
  "/about": ["About | HCP Voice", "Built for scientific meaning — not information volume. HCP Voice is developed by MarksMan Healthcare."],
  "/request-demo": ["Request a Demo | HCP Voice", "Explore HCP Voice for your asset or clinical question. Request a demonstration or discuss a pilot assessment."],
};

const Logo = ({ dark = true }) => (
  <Link to="/" className="flex items-center gap-2.5" aria-label="HCP Voice home">
    <svg width="36" height="36" viewBox="0 0 40 40" role="img" aria-label="HCP Voice icon — voice nodes connecting into a calibration ring emitting one clear signal">
      <rect x="1" y="1" width="38" height="38" rx="11" fill="#0C2244" />
      <circle cx="10" cy="11" r="2" fill="#5EEAD4" />
      <circle cx="9" cy="21" r="2" fill="#2DD4BF" />
      <circle cx="11" cy="30" r="2" fill="#0D9488" />
      <path d="M12 11.5 L21.5 19 M11 21 L21 20 M12.8 29 L21.5 21" stroke="#134E4A" strokeWidth="1.3" />
      <circle cx="23" cy="20" r="4" fill="none" stroke="#2DD4BF" strokeWidth="1.8" />
      <path d="M27.5 20 L33 20" stroke="#F8FAFC" strokeWidth="2" strokeLinecap="round" />
      <circle cx="33" cy="20" r="1.6" fill="#F8FAFC" />
    </svg>
    <span>
      <span className={`font-heading font-extrabold text-lg leading-none block ${dark ? "text-white" : "text-slate-900"}`}>HCP Voice<span className="text-teal-400">™</span></span>
      <span className={`text-[8.5px] font-mono uppercase tracking-widest ${dark ? "text-slate-400" : "text-slate-500"} hidden sm:block mt-1.5`}>Evidence-Calibrated HCP Intelligence</span>
    </span>
  </Link>
);

const NAV = [["Home", "/"], ["Platform", "/platform"], ["How It Works", "/how-it-works"], ["Applications", "/applications"], ["About", "/about"]];

function SiteHeader() {
  const [open, setOpen] = useState(false);
  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-slate-950/90 backdrop-blur-xl border-b border-teal-500/15" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 lg:px-6 h-16 flex items-center justify-between gap-4">
        <Logo />
        <nav className="hidden lg:flex items-center gap-7" aria-label="Primary">
          {NAV.map(([l, to]) => (
            <NavLink key={to} to={to} end={to === "/"} data-testid={`nav-site-${l.toLowerCase().replace(/\W+/g, "-")}`}
              className={({ isActive }) => `text-sm font-medium transition-colors relative py-1 ${isActive ? "text-teal-300" : "text-slate-300 hover:text-white"}`}>{l}</NavLink>
          ))}
        </nav>
        <div className="hidden lg:flex items-center gap-2.5">
          <Link to="/request-demo" className="sv-glow-btn text-sm font-semibold text-slate-900 bg-teal-400 hover:bg-teal-300 rounded-full px-4 py-1.5" data-testid="header-demo-btn">Request a Demo</Link>
        </div>
        <button className="lg:hidden p-2 text-white" onClick={() => setOpen(!open)} aria-label="Toggle menu" data-testid="mobile-menu-btn">
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>
      {open && (
        <div className="lg:hidden bg-slate-950 border-t border-slate-800 px-4 py-4 space-y-2.5" data-testid="mobile-menu">
          {NAV.map(([l, to]) => <NavLink key={to} to={to} end={to === "/"} onClick={() => setOpen(false)} className="block text-sm font-medium text-slate-200 py-1">{l}</NavLink>)}
          <Link to="/request-demo" onClick={() => setOpen(false)} className="inline-block text-sm font-semibold text-slate-900 bg-teal-400 rounded-full px-4 py-1.5">Request a Demo</Link>
        </div>
      )}
    </header>
  );
}

function SiteFooter() {
  const cols = [
    ["Product", [["Platform", "/platform"], ["How It Works", "/how-it-works"], ["Applications", "/applications"], ["Request a Demo", "/request-demo"]]],
    ["Company", [["About", "/about"], ["MarksMan Healthcare", "/about"], ["Contact", "/request-demo"], ["Logo concepts", "/logo-variants"]]],
    ["Legal", [["Privacy Policy", "/privacy"], ["Terms of Use", "/terms"], ["Cookie Policy", "/cookies"]]],
  ];
  return (
    <footer className="bg-slate-950 text-slate-300 mt-24 border-t border-teal-500/10" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 lg:px-6 py-14 grid grid-cols-2 md:grid-cols-4 gap-10">
        <div className="col-span-2 md:col-span-1">
          <Logo dark />
          <p className="text-xs text-slate-400 leading-relaxed mt-4 max-w-[220px]">The collective clinical voice, calibrated against evidence and translated into strategy.</p>
        </div>
        {cols.map(([title, links]) => (
          <div key={title}>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-teal-500/80 mb-3">{title}</p>
            <ul className="space-y-2">
              {links.map(([l, to]) => <li key={l}><Link to={to} className="text-sm text-slate-400 hover:text-teal-300 transition-colors">{l}</Link></li>)}
            </ul>
          </div>
        ))}
      </div>
      <div className="border-t border-slate-800/80 py-5">
        <div className="max-w-7xl mx-auto px-4 lg:px-6 flex flex-col sm:flex-row justify-between gap-2">
          <p className="text-xs text-slate-400">HCP Voice™ is an evidence-calibrated healthcare-professional intelligence solution developed by MarksMan Healthcare.</p>
          <p className="text-xs text-slate-400 shrink-0">© 2026 MarksMan Healthcare. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
}

const DarkHero = ({ kicker, title, copy, children }) => (
  <section className="pt-28 pb-16 lg:pt-36 lg:pb-20 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 relative overflow-hidden">
    <div className="absolute inset-0 sv-grid-bg opacity-40" aria-hidden="true" />
    <div className="max-w-7xl mx-auto px-4 lg:px-6 relative">
      <Reveal>
        {kicker && <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-teal-400 mb-4">{kicker}</p>}
        <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-white leading-[1.08] max-w-3xl">{title}</h1>
        {copy && <p className="text-base text-slate-300 leading-relaxed mt-5 max-w-2xl">{copy}</p>}
      </Reveal>
      {children}
    </div>
  </section>
);

const Section = ({ kicker, title, copy, children, dark, wide }) => (
  <section className={`py-16 lg:py-20 ${dark ? "bg-slate-950" : ""}`}>
    <div className="max-w-7xl mx-auto px-4 lg:px-6">
      <Reveal>
        {kicker && <p className={`font-mono text-[10px] uppercase tracking-[0.22em] mb-3 ${dark ? "text-teal-400" : "text-teal-700"}`}>{kicker}</p>}
        {title && <h2 className={`font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight ${dark ? "text-white" : "text-slate-900"}`}>{title}</h2>}
        {copy && <p className={`text-sm leading-relaxed mt-3 ${wide ? "max-w-4xl" : "max-w-3xl"} ${dark ? "text-slate-400" : "text-slate-600"}`}>{copy}</p>}
      </Reveal>
      <div className="mt-9">{children}</div>
    </div>
  </section>
);

const Card = ({ icon: Icon, title, copy, delay = 0 }) => (
  <Reveal delay={delay} className="h-full">
    <div className="sv-card h-full bg-white border border-slate-200/80 rounded-2xl p-6 shadow-sm hover:border-teal-400/60">
      {Icon && <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-50 to-teal-100/60 border border-teal-200/70 flex items-center justify-center mb-4"><Icon className="w-4.5 h-4.5 text-teal-700" style={{ width: 18, height: 18 }} /></div>}
      <p className="text-sm font-bold text-slate-900">{title}</p>
      {copy && <p className="text-[13px] text-slate-600 leading-relaxed mt-2">{copy}</p>}
    </div>
  </Reveal>
);

const CTA = ({ title, copy, buttons }) => (
  <section className="py-20 bg-slate-950 relative overflow-hidden">
    <div className="absolute inset-0 sv-grid-bg opacity-30" aria-hidden="true" />
    <div className="max-w-4xl mx-auto px-4 relative">
      <Reveal>
        <div className="sv-trace rounded-3xl px-8 py-12 sm:px-14 text-center">
          <Sparkles className="w-5 h-5 text-teal-400 mx-auto mb-4" aria-hidden="true" />
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white tracking-tight">{title}</h2>
          {copy && <p className="text-sm text-slate-400 mt-4 max-w-2xl mx-auto leading-relaxed">{copy}</p>}
          <div className="flex flex-wrap justify-center gap-3 mt-8">
            {buttons.map(([label, to, primary]) => (
              <Link key={label} to={to} data-testid={`cta-${label.toLowerCase().replace(/\W+/g, "-")}`}
                className={primary ? "sv-glow-btn text-sm font-semibold text-slate-900 bg-teal-400 hover:bg-teal-300 rounded-full px-7 py-3" : "text-sm font-medium text-slate-200 border border-slate-600 hover:border-teal-400 hover:text-teal-300 rounded-full px-7 py-3 transition-colors"}>{label}</Link>
            ))}
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

const STATS = [["5", "+", "Evidence streams unified"], ["100", "%", "Source-linked findings"], ["4", "", "Calibration dimensions"], ["13", "", "Intelligence modules"]];

function Home() {
  return (
    <>
      <section className="pt-28 lg:pt-32 pb-16 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 relative overflow-hidden">
        <div className="absolute inset-0 sv-grid-bg opacity-40" aria-hidden="true" />
        <div className="max-w-7xl mx-auto px-4 lg:px-6 relative grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <Reveal>
              <p className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-teal-300 bg-teal-500/10 border border-teal-500/30 rounded-full px-3.5 py-1.5 mb-6">
                <span className="w-1.5 h-1.5 rounded-full bg-teal-400 animate-pulse" />Evidence-Calibrated HCP Intelligence
              </p>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.05]">
                From HCP voice to <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-300 to-teal-500">strategic clarity.</span>
              </h1>
              <p className="text-base text-slate-300 leading-relaxed mt-6 max-w-xl">
                Healthcare-professional perspectives are distributed across publications, congresses, scientific discussions, educational programmes and digital channels. HCP Voice brings these signals together, calibrates them against scientific evidence and translates them into intelligence that life-sciences teams can act upon.
              </p>
              <div className="flex flex-wrap gap-3 mt-8">
                <Link to="/request-demo" data-testid="hero-demo-btn" className="sv-glow-btn text-sm font-semibold text-slate-900 bg-teal-400 hover:bg-teal-300 rounded-full px-7 py-3">Request a Demo</Link>
                <Link to="/platform" data-testid="hero-platform-btn" className="text-sm font-medium text-slate-200 border border-slate-600 hover:border-teal-400 hover:text-teal-300 rounded-full px-7 py-3 transition-colors">Explore the Platform</Link>
              </div>
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-8">
                {["Source-traceable", "Expert-reviewed", "AI-assisted"].map((b) => (
                  <span key={b} className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest text-slate-400"><CheckCircle2 className="w-3.5 h-3.5 text-teal-500" />{b}</span>
                ))}
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-6">
            <Reveal delay={150}>
              <div className="rounded-3xl border border-teal-500/20 bg-slate-950/60 backdrop-blur-md shadow-2xl overflow-hidden">
                <div className="flex items-center justify-between px-5 py-3 border-b border-slate-800">
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Signal convergence · live view</p>
                  <span className="font-mono text-[9px] uppercase tracking-widest text-teal-400 bg-teal-500/10 border border-teal-500/30 rounded-full px-2 py-0.5">Calibrating</span>
                </div>
                <div className="h-72 sm:h-80">
                  <NodeCanvas labels={["Publications", "Congresses", "Discussions", "Education", "Client evidence"]} />
                </div>
              </div>
            </Reveal>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 lg:px-6 relative mt-14">
          <Reveal delay={100}>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-slate-800/80 rounded-2xl overflow-hidden border border-slate-800" data-testid="hero-stats">
              {STATS.map(([n, sfx, l]) => (
                <div key={l} className="bg-slate-900/90 px-6 py-5">
                  <p className="font-heading text-3xl font-extrabold text-teal-300"><Counter to={parseInt(n, 10)} suffix={sfx} /></p>
                  <p className="text-[11px] font-mono uppercase tracking-widest text-slate-400 mt-1">{l}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <Section kicker="The challenge" title="More information does not always mean greater insight."
        copy="HCP conversations are fragmented, context-dependent and often difficult to interpret at scale. Conventional monitoring may reveal what is being discussed, but it does not always explain why HCPs hold particular views, how strongly those views are supported by evidence, or what they mean for strategy.">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[["Fragmented HCP conversations", "Signals scattered across journals, congresses and digital channels."],
            ["Uncalibrated sentiment", "Positive or negative labels without clinical conviction or context."],
            ["Disconnected scientific evidence", "Perceptions analysed in isolation from the underlying data."],
            ["Limited strategic interpretation", "Activity reports that stop short of recommending action."]].map(([t, c], i) => <Card key={t} title={t} copy={c} delay={i * 70} />)}
        </div>
      </Section>

      <Section dark kicker="The intelligence ecosystem" title="One intelligence picture. Multiple evidence streams."
        copy="HCP Voice synthesises the collective clinical voice and connects it with the broader scientific and competitive landscape.">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4" data-testid="ecosystem-bento">
          {[["HCP Intelligence", MessagesSquare, "Opinions, questions, concerns and clinical reasoning across professional segments.", "md:col-span-8"],
            ["Scientific Evidence", FlaskConical, "Publications, guidelines and trial data.", "md:col-span-4"],
            ["Congress Intelligence", Presentation, "Abstracts, posters and proceedings.", "md:col-span-4"],
            ["Educational Intelligence", GraduationCap, "CME programmes, webinars and symposia.", "md:col-span-4"],
            ["Competitive Intelligence", Crosshair, "Competitor positioning and emerging narratives.", "md:col-span-4"]].map(([t, I, c, span], i) => (
            <Reveal key={t} delay={i * 60} className={span}>
              <div className="sv-card h-full rounded-2xl bg-slate-900/90 border border-teal-500/15 hover:border-teal-500/45 backdrop-blur-md p-6">
                <div className="w-10 h-10 rounded-xl bg-teal-500/10 border border-teal-500/30 flex items-center justify-center mb-4"><I className="text-teal-400" style={{ width: 18, height: 18 }} /></div>
                <p className="text-sm font-bold text-white">{t}</p>
                <p className="text-[13px] text-slate-400 leading-relaxed mt-2">{c}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={200}>
          <div className="flex items-center justify-center gap-3 mt-8">
            <GitMerge className="w-4 h-4 text-teal-400" />
            <p className="text-sm font-bold text-white">Actionable Strategic Intelligence</p>
          </div>
        </Reveal>
      </Section>

      <Section kicker="Outcomes" title="Move from observation to action.">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {["Understand evolving HCP perceptions", "Explain treatment-adoption behaviour", "Identify barriers and accelerators", "Reveal consensus and controversy", "Detect evidence and education gaps", "Inform medical and strategic priorities"].map((t, i) => <Card key={t} icon={CheckCircle2} title={t} delay={i * 60} />)}
        </div>
      </Section>

      <CTA title="What is the clinical voice telling you?"
        copy="Explore how HCP Voice can create a focused, evidence-calibrated intelligence assessment for your asset, indication or therapeutic landscape."
        buttons={[["Discuss a Pilot Assessment", "/request-demo", true]]} />
    </>
  );
}

function Platform() {
  const caps = [
    ["HCP Voice", "Synthesises HCP opinions, questions, concerns and clinical reasoning across relevant sources and professional segments.", MessagesSquare],
    ["Clinical Themes", "Identifies the scientific and practical themes shaping treatment perceptions and clinical behaviour.", GitMerge],
    ["Adoption Journey", "Maps barriers, accelerators, questions and evidence needs across the path from awareness to clinical use.", ArrowRight],
    ["Evidence Landscape", "Connects HCP perceptions with publications, guidelines, trials and other relevant scientific evidence.", FlaskConical],
    ["Consensus and Controversies", "Shows where HCPs agree, disagree or remain uncertain—and why those differences matter.", Users],
    ["Strategic Implications", "Translates validated intelligence into function-specific considerations and recommended actions.", Target],
  ];
  const rows = [["Counts mentions", "Interprets clinical meaning"], ["Labels sentiment", "Assesses position and conviction"], ["Presents individual comments", "Synthesises the collective voice"], ["Separates voice from evidence", "Connects perceptions with evidence"], ["Reports activity", "Identifies strategic implications"], ["Focuses on volume", "Prioritises relevance and confidence"]];
  return (
    <>
      <DarkHero kicker="Platform" title="Healthcare-professional intelligence, scientifically calibrated."
        copy="HCP Voice is designed to interpret the collective clinical conversation—not simply count mentions or classify comments as positive or negative." />
      <Section kicker="Capabilities" title="The intelligence framework">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">{caps.map(([t, c, I], i) => <Card key={t} icon={I} title={t} copy={c} delay={i * 60} />)}</div>
      </Section>
      <Section dark kicker="Differentiation" title="Beyond conventional sentiment analysis">
        <Reveal>
          <div className="overflow-x-auto rounded-2xl border border-teal-500/20 shadow-xl">
            <table className="w-full text-sm bg-slate-900/90" data-testid="comparison-table">
              <thead><tr className="text-left border-b border-slate-800">
                <th className="px-6 py-4 font-semibold text-slate-400 text-xs font-mono uppercase tracking-[0.18em]">Conventional monitoring</th>
                <th className="px-6 py-4 font-semibold text-teal-300 text-xs font-mono uppercase tracking-[0.18em]">HCP Voice</th>
              </tr></thead>
              <tbody>{rows.map(([a, b]) => (
                <tr key={a} className="border-t border-slate-800/70 hover:bg-slate-800/30 transition-colors">
                  <td className="px-6 py-3.5 text-slate-400">{a}</td>
                  <td className="px-6 py-3.5 text-white font-medium"><span className="inline-flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-teal-400 shrink-0" />{b}</span></td>
                </tr>))}</tbody>
            </table>
          </div>
        </Reveal>
      </Section>
      <Section kicker="Intelligence questions" title="What the platform can reveal">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {["Which patients do HCPs consider most appropriate?", "What is influencing confidence in treatment use?", "Where does clinical perception align with evidence?", "Which issues are creating uncertainty or disagreement?", "How do perspectives vary across markets and HCP segments?", "What evidence, education or engagement may be required?"].map((q, i) => (
            <Reveal key={q} delay={i * 50}>
              <div className="sv-card flex items-start gap-3 bg-white border border-slate-200/80 rounded-2xl px-5 py-4 shadow-sm hover:border-teal-400/60">
                <Quote className="w-3.5 h-3.5 text-teal-500 mt-0.5 shrink-0" /><p className="text-sm text-slate-700">{q}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>
      <CTA title="See the intelligence behind the conversation." buttons={[["Request a Demo", "/request-demo", true], ["See How It Works", "/how-it-works", false]]} />
    </>
  );
}

function HowItWorks() {
  const steps = [
    ["Define", "Define the asset, indication, geographies, HCP segments, competitors and priority intelligence questions."],
    ["Gather", "Bring together relevant HCP conversations, scientific publications, congress materials, educational content and client-provided evidence."],
    ["Calibrate", "Analyse HCP positions, supporting evidence, source quality, level of consensus, clinical relevance and confidence."],
    ["Translate", "Convert validated findings into strategic implications and recommended actions for relevant life-sciences functions."],
  ];
  const sources = [
    ["Scientific evidence", "Publications, literature reviews, guidelines, regulatory documents and clinical-trial evidence.", FlaskConical],
    ["HCP discussions", "Published interviews, expert discussions, professional digital channels and legitimate HCP sources.", MessagesSquare],
    ["Congresses", "Abstracts, posters, presentations, proceedings, transcripts and authorised on-site intelligence.", Presentation],
    ["Scientific education", "CME programmes, webinars, symposia, podcasts and educational discussions.", GraduationCap],
    ["Client evidence", "Advisory-board reports, field insights, commissioned research and other authorised internal materials.", ShieldCheck],
  ];
  return (
    <>
      <DarkHero kicker="Methodology" title="From fragmented evidence to a coherent clinical voice."
        copy="Each HCP Voice assessment follows a transparent, expert-reviewed process designed around the client's strategic questions." />
      <Section kicker="Process" title="Four-step process">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {steps.map(([t, c], i) => (
            <Reveal key={t} delay={i * 90}>
              <div className="sv-card relative h-full bg-white border border-slate-200/80 rounded-2xl p-6 shadow-sm hover:border-teal-400/60 overflow-hidden">
                <span className="font-heading text-6xl font-extrabold text-teal-500/10 absolute -top-2 right-3 select-none" aria-hidden="true">{i + 1}</span>
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-teal-600">Step {i + 1}</span>
                <p className="text-base font-bold text-slate-900 mt-1.5">{t}</p>
                <p className="text-[13px] text-slate-600 leading-relaxed mt-2">{c}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>
      <Section dark kicker="Inputs" title="Intelligence sources">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {sources.map(([t, c, I], i) => (
            <Reveal key={t} delay={i * 60}>
              <div className="sv-card h-full rounded-2xl bg-slate-900/90 border border-teal-500/15 hover:border-teal-500/45 p-6">
                <div className="w-10 h-10 rounded-xl bg-teal-500/10 border border-teal-500/30 flex items-center justify-center mb-4"><I className="text-teal-400" style={{ width: 18, height: 18 }} /></div>
                <p className="text-sm font-bold text-white">{t}</p>
                <p className="text-[13px] text-slate-400 leading-relaxed mt-2">{c}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={200}>
          <p className="text-xs text-slate-400 bg-slate-900/70 border border-teal-500/15 rounded-xl px-5 py-3.5 mt-5 max-w-3xl" data-testid="sources-note">
            HCP Voice uses publicly available, legitimately accessed, licensed or client-provided sources. It does not bypass access restrictions or infer private HCP views.
          </p>
        </Reveal>
      </Section>
      <Section kicker="Governance" title="AI-assisted. Expert-reviewed. Source-traceable."
        copy="Technology helps organise and interpret large volumes of information. MarksMan scientific experts review important findings, evidence connections and strategic implications before they are included in approved outputs.">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[["Every important finding links back to its source", Link2], ["AI-generated interpretations remain distinguishable from verified evidence", ShieldCheck], ["Strategic recommendations undergo expert review", CheckCircle2]].map(([t, I], i) => <Card key={t} icon={I} title={t} delay={i * 70} />)}
        </div>
      </Section>
      <Section kicker="Engagement models" title="Assessment options">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-4xl">
          <Reveal>
            <div className="sv-trace-light sv-card rounded-2xl p-6 shadow-md relative h-full">
              <span className="absolute -top-2.5 left-5 text-[9px] font-mono uppercase tracking-widest bg-teal-700 text-white rounded-full px-3 py-1">Primary current offering</span>
              <p className="text-base font-bold text-slate-900 mt-2">Cross-sectional assessment</p>
              <p className="text-[13px] text-slate-600 mt-2 leading-relaxed">A focused intelligence snapshot based on a defined evidence cut-off date.</p>
            </div>
          </Reveal>
          <Reveal delay={90}>
            <div className="sv-card bg-white border border-slate-200/80 rounded-2xl p-6 shadow-sm h-full">
              <p className="text-base font-bold text-slate-900 mt-2">Ongoing intelligence</p>
              <p className="text-[13px] text-slate-600 mt-2 leading-relaxed">Future-ready monthly, bimonthly, quarterly or congress-triggered updates.</p>
            </div>
          </Reveal>
        </div>
      </Section>
      <CTA title="A method your scientific team can stand behind."
        copy="Transparent process. Traceable evidence. Expert review at every consequential step."
        buttons={[["Request a Demo", "/request-demo", true]]} />
    </>
  );
}

function Applications() {
  const fns = [
    ["Medical Affairs", "Identify scientific questions, educational needs, evidence gaps and priorities for scientific exchange.", MessagesSquare],
    ["Clinical Development and Evidence Generation", "Understand unresolved clinical questions and identify evidence that may strengthen future confidence.", FlaskConical],
    ["Competitive Intelligence", "Track changes in treatment perceptions, competitor positioning and emerging clinical narratives.", Crosshair],
    ["Market Access", "Explore HCP perspectives related to access, testing, treatment pathways and real-world implementation.", Link2],
    ["Brand and Commercial Strategy", "Understand the clinical factors influencing treatment consideration, adoption and differentiation.", Target],
    ["Senior Leadership", "Provide a concise view of the most strategically important HCP signals, risks and opportunities.", Users],
  ];
  return (
    <>
      <DarkHero kicker="Applications" title="Designed for decisions across the product lifecycle."
        copy="HCP Voice can support multiple life-sciences functions while maintaining one consistent, evidence-linked intelligence foundation." />
      <Section kicker="Functions" title="Functional applications">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">{fns.map(([t, c, I], i) => <Card key={t} icon={I} title={t} copy={c} delay={i * 60} />)}</div>
      </Section>
      <Section dark kicker="Strategic questions" title="Example strategic questions">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {["Which patient groups are considered most appropriate for the treatment?", "What barriers are limiting adoption?", "Where do academic and community perspectives differ?", "How is the asset perceived against emerging alternatives?", "Which evidence gaps matter most to HCPs?", "What could change treatment behaviour?", "How are HCPs interpreting recent congress and publication readouts?", "What should Medical Affairs prioritise next?"].map((q, i) => (
            <Reveal key={q} delay={i * 45}>
              <div className="sv-card flex items-start gap-3 rounded-2xl bg-slate-900/90 border border-teal-500/15 hover:border-teal-500/45 px-5 py-4">
                <Quote className="w-3.5 h-3.5 text-teal-400 mt-0.5 shrink-0" /><p className="text-sm text-slate-200">{q}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>
      <Section kicker="Configuration" title="Flexible project scope"
        copy="No two intelligence questions are the same. Each HCP Voice assessment is scoped around the decision it needs to inform — configured across ten dimensions, individually or in combination.">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {[
            [Pill, "Asset", "A single product or portfolio of interest"],
            [Stethoscope, "Indication", "A defined disease setting or patient population"],
            [Microscope, "Therapeutic area", "A broader clinical field or specialty"],
            [Globe2, "Geography", "Global, regional or market-specific views"],
            [UserRound, "HCP segment", "Academic, community or specialist voices"],
            [ListOrdered, "Treatment line", "Early-line, later-line or sequencing focus"],
            [Dna, "Biomarker", "Biomarker-defined subgroups and testing context"],
            [Scale, "Competitor", "Named comparators or the emerging landscape"],
            [Presentation, "Congress", "A specific meeting, session or data readout"],
            [HelpCircle, "Strategic question", "A decision the assessment must inform"],
          ].map(([I, t, d], i) => (
            <Reveal key={t} delay={i * 40}>
              <div className="group h-full rounded-2xl bg-white border border-slate-200 hover:border-teal-400 hover:shadow-md transition-all duration-200 px-4 py-4">
                <I className="w-[18px] h-[18px] text-teal-600 group-hover:scale-110 transition-transform" />
                <p className="text-sm font-semibold text-slate-900 mt-2.5">{t}</p>
                <p className="text-xs text-slate-500 leading-relaxed mt-1">{d}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={200}>
          <p className="text-xs text-slate-500 mt-5 flex items-center gap-2"><GitMerge className="w-3.5 h-3.5 text-teal-600 shrink-0" />Dimensions can be combined — for example, one asset, one biomarker-defined population and two comparators across three markets — within a single assessment.</p>
        </Reveal>
      </Section>
      <CTA title="Start with the question that matters most."
        copy="HCP Voice can be configured around a specific asset, clinical uncertainty, competitive event or strategic decision."
        buttons={[["Discuss Your Intelligence Need", "/request-demo", true]]} />
    </>
  );
}

function About() {
  return (
    <>
      <DarkHero kicker="About" title="Built for scientific meaning—not information volume." />
      <Section kicker="Origin" title="Why HCP Voice">
        <Reveal>
          <div className="max-w-3xl space-y-4">
            <p className="text-[15px] text-slate-600 leading-relaxed">HCP Voice was created to address a recurring challenge in life sciences: important clinical perspectives are available across many sources, but their strategic meaning is often difficult to see.</p>
            <p className="text-[15px] text-slate-600 leading-relaxed">The platform brings HCP conversations and scientific evidence into one structured intelligence environment, helping teams understand not only what is being said, but why it matters.</p>
          </div>
        </Reveal>
      </Section>
      <Section dark kicker="Principles" title="Our principles">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[["Scientifically grounded", "Insights are interpreted within their clinical and evidence context.", FlaskConical],
            ["Source traceable", "Important findings can be traced to the underlying evidence or conversation.", Link2],
            ["Collectively interpreted", "The platform prioritises patterns across relevant HCP voices rather than overemphasising isolated opinions.", Users],
            ["Action oriented", "Every assessment is designed to inform a question, decision or strategic response.", Target]].map(([t, c, I], i) => (
            <Reveal key={t} delay={i * 70}>
              <div className="sv-card h-full rounded-2xl bg-slate-900/90 border border-teal-500/15 hover:border-teal-500/45 p-6">
                <div className="w-10 h-10 rounded-xl bg-teal-500/10 border border-teal-500/30 flex items-center justify-center mb-4"><I className="text-teal-400" style={{ width: 18, height: 18 }} /></div>
                <p className="text-sm font-bold text-white">{t}</p>
                <p className="text-[13px] text-slate-400 leading-relaxed mt-2">{c}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>
      <Section kicker="The company" title="Developed by MarksMan Healthcare">
        <Reveal>
          <div className="max-w-3xl">
            <p className="text-[15px] text-slate-600 leading-relaxed">HCP Voice is developed by MarksMan Healthcare, a healthcare consulting and evidence-solutions organisation working across Medical Affairs, real-world evidence, HEOR, evidence synthesis, market access and life-sciences strategy.</p>
            <a href="https://www.marksmanhealthcare.com" target="_blank" rel="noreferrer" data-testid="marksman-link"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-teal-700 hover:text-teal-800 mt-4 group">Visit MarksMan Healthcare <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" /></a>
          </div>
        </Reveal>
      </Section>
      <CTA title="The collective clinical voice becomes more valuable when it is connected to evidence." buttons={[["Request a Conversation", "/request-demo", true]]} />
    </>
  );
}

const FUNCTIONS = ["Global Medical Affairs", "Regional or Local Medical Affairs", "Field Medical/MSL", "Clinical Development", "Evidence Generation/HEOR", "Market Access", "Competitive Intelligence", "Brand or Commercial Strategy", "Senior Leadership", "Other"];
const DISCUSSIONS = ["Platform demonstration", "Pilot assessment", "Congress intelligence", "Competitive intelligence", "General discussion"];
const F0 = { full_name: "", job_title: "", organisation: "", email: "", country: "", function: "", therapeutic_area: "", asset_indication: "", intelligence_question: "", discussion_type: "", consent: false };

function RequestDemo() {
  const [f, setF] = useState(F0);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e }));

  const submit = (e) => {
    e.preventDefault();
    setError("");
    if (!f.full_name || !f.email) { setError("Please provide your full name and business email."); return; }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email)) { setError("Please enter a valid business email address."); return; }
    if (!f.consent) { setError("Please confirm your consent so we can respond to your enquiry."); return; }
    setBusy(true);
    api.post("/demo-requests", f).then(() => setDone(true))
      .catch((err) => setError(err.response?.data?.detail || "Submission failed — please try again."))
      .finally(() => setBusy(false));
  };

  const inputCls = "w-full text-sm border border-slate-300 rounded-lg px-3.5 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-shadow disabled:opacity-50";
  return (
    <>
      <DarkHero kicker="Request a demo" title="Explore HCP Voice for your asset or clinical question."
        copy="Tell us what you would like to understand. Our team will discuss how a focused HCP Voice assessment could be structured around your strategic priorities." />
      <section className="pb-8 -mt-6 relative z-10">
        <div className="max-w-2xl mx-auto px-4">
          {done ? (
            <Reveal>
              <div className="sv-trace-light rounded-2xl p-10 text-center shadow-xl" data-testid="demo-success">
                <CheckCircle2 className="w-9 h-9 text-teal-600 mx-auto mb-4" />
                <p className="text-base font-semibold text-slate-900">Thank you. Your request has been received, and the HCP Voice team will contact you shortly.</p>
              </div>
            </Reveal>
          ) : (
            <Reveal>
              <form onSubmit={submit} className="sv-trace-light rounded-2xl p-7 sm:p-8 shadow-xl grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="demo-form" noValidate>
                {[["full_name", "Full name *"], ["job_title", "Job title"], ["organisation", "Organisation"], ["email", "Business email *"], ["country", "Country"], ["therapeutic_area", "Therapeutic area"]].map(([k, l]) => (
                  <label key={k} className="text-xs font-semibold text-slate-700">
                    {l}
                    <input type={k === "email" ? "email" : "text"} value={f[k]} onChange={set(k)} className={`${inputCls} mt-1.5`} data-testid={`demo-${k}`} required={l.includes("*")} />
                  </label>
                ))}
                <label className="text-xs font-semibold text-slate-700">Function
                  <select value={f.function} onChange={set("function")} className={`${inputCls} mt-1.5`} data-testid="demo-function">
                    <option value="">Select…</option>{FUNCTIONS.map((o) => <option key={o}>{o}</option>)}
                  </select>
                </label>
                <label className="text-xs font-semibold text-slate-700">Preferred type of discussion
                  <select value={f.discussion_type} onChange={set("discussion_type")} className={`${inputCls} mt-1.5`} data-testid="demo-discussion">
                    <option value="">Select…</option>{DISCUSSIONS.map((o) => <option key={o}>{o}</option>)}
                  </select>
                </label>
                <label className="text-xs font-semibold text-slate-700 sm:col-span-2">Asset or indication
                  <input value={f.asset_indication} onChange={set("asset_indication")} className={`${inputCls} mt-1.5`} data-testid="demo-asset" />
                </label>
                <label className="text-xs font-semibold text-slate-700 sm:col-span-2">Primary intelligence question
                  <textarea value={f.intelligence_question} onChange={set("intelligence_question")} rows={3} className={`${inputCls} mt-1.5`} data-testid="demo-question" />
                </label>
                <label className="flex items-start gap-2.5 text-xs text-slate-600 sm:col-span-2">
                  <input type="checkbox" checked={f.consent} onChange={set("consent")} className="mt-0.5 accent-teal-600" data-testid="demo-consent" />
                  I consent to HCP Voice and MarksMan Healthcare contacting me about this enquiry. *
                </label>
                {error && <p className="text-xs text-red-600 sm:col-span-2" role="alert" data-testid="demo-error">{error}</p>}
                <div className="sm:col-span-2">
                  <button type="submit" disabled={busy} data-testid="demo-submit"
                    className="sv-glow-btn text-sm font-semibold text-slate-900 bg-teal-400 hover:bg-teal-300 focus:ring-2 focus:ring-teal-500 disabled:opacity-50 rounded-full px-9 py-3">
                    {busy ? "Sending…" : "Request a Demo"}
                  </button>
                  <p className="text-xs text-slate-500 mt-4">Prefer email? Contact <a href="mailto:enquiry@marksmanhealthcare.com" className="text-teal-700 font-medium">enquiry@marksmanhealthcare.com</a></p>
                </div>
              </form>
            </Reveal>
          )}
        </div>
      </section>
    </>
  );
}

const Legal = ({ title }) => (
  <DarkHero title={title} copy={`This ${title.toLowerCase()} is a placeholder for the HCP Voice prototype website. Final legal content will be supplied by MarksMan Healthcare before public launch. For any questions, contact enquiry@marksmanhealthcare.com.`} />
);

const VARIANTS = [
  ["A — Convergence Arrow", <svg key="a" width="72" height="72" viewBox="0 0 40 40"><rect x="1" y="1" width="38" height="38" rx="11" fill="#0C2244"/><path d="M8 12.5 C15 12.5 16 20 21 20" stroke="#5EEAD4" strokeWidth="1.7" fill="none" strokeLinecap="round"/><path d="M8 20 L21 20" stroke="#2DD4BF" strokeWidth="1.7" strokeLinecap="round"/><path d="M8 27.5 C15 27.5 16 20 21 20" stroke="#0D9488" strokeWidth="1.7" fill="none" strokeLinecap="round"/><circle cx="23" cy="20" r="3.4" fill="none" stroke="#2DD4BF" strokeWidth="1.7"/><path d="M26.8 20 H30.5" stroke="#F8FAFC" strokeWidth="1.9" strokeLinecap="round"/><path d="M29 16.6 L32.6 20 L29 23.4" stroke="#F8FAFC" strokeWidth="1.9" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg>],
  ["B — Calibration Rings", <svg key="b" width="72" height="72" viewBox="0 0 40 40"><circle cx="20" cy="20" r="18" fill="#0C2244"/><path d="M9 20 A11 11 0 0 1 31 20" stroke="#134E4A" strokeWidth="1.6" fill="none"/><path d="M12.5 20 A7.5 7.5 0 0 1 27.5 20" stroke="#0D9488" strokeWidth="1.8" fill="none"/><path d="M16 20 A4 4 0 0 1 24 20" stroke="#2DD4BF" strokeWidth="2" fill="none"/><circle cx="20" cy="20" r="2.2" fill="#5EEAD4"/><path d="M20 22.5 V29" stroke="#F8FAFC" strokeWidth="1.9" strokeLinecap="round"/></svg>],
  ["C — Evidence Weave", <svg key="c" width="72" height="72" viewBox="0 0 40 40"><rect x="1" y="1" width="38" height="38" rx="19" fill="#0C2244"/><path d="M9 27 C15 27 17 13 25 13 C29 13 31 15.5 31 18" stroke="#2DD4BF" strokeWidth="2" fill="none" strokeLinecap="round"/><path d="M9 13 C15 13 17 27 25 27 C29 27 31 24.5 31 22" stroke="#5EEAD4" strokeWidth="2" fill="none" strokeLinecap="round"/><circle cx="20" cy="20" r="2.6" fill="#F8FAFC"/></svg>],
  ["D — Signal Prism", <svg key="d" width="72" height="72" viewBox="0 0 40 40"><rect x="1" y="1" width="38" height="38" rx="11" fill="#0C2244"/><path d="M7 11 L18 20 L7 29" stroke="#0D9488" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round"/><path d="M12 11 L23 20 L12 29" stroke="#2DD4BF" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round"/><path d="M17 11 L28 20 L17 29" stroke="#5EEAD4" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round"/><rect x="28.5" y="17.5" width="5" height="5" rx="1.2" transform="rotate(45 31 20)" fill="#F8FAFC"/></svg>],
  ["E — Node Constellation (current)", <svg key="e" width="72" height="72" viewBox="0 0 40 40"><rect x="1" y="1" width="38" height="38" rx="11" fill="#0C2244"/><circle cx="10" cy="11" r="2" fill="#5EEAD4"/><circle cx="9" cy="21" r="2" fill="#2DD4BF"/><circle cx="11" cy="30" r="2" fill="#0D9488"/><path d="M12 11.5 L21.5 19 M11 21 L21 20 M12.8 29 L21.5 21" stroke="#134E4A" strokeWidth="1.3"/><circle cx="23" cy="20" r="4" fill="none" stroke="#2DD4BF" strokeWidth="1.8"/><path d="M27.5 20 L33 20" stroke="#F8FAFC" strokeWidth="2" strokeLinecap="round"/><circle cx="33" cy="20" r="1.6" fill="#F8FAFC"/></svg>],
];

function LogoVariants() {
  return (
    <DarkHero title="Logo icon concepts" copy="Five alternative marks — each expressing voices converging, evidence connection and strategic clarity.">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mt-10" data-testid="logo-variants">
        {VARIANTS.map(([name, svg]) => (
          <div key={name} className="sv-card bg-slate-900/90 border border-teal-500/15 hover:border-teal-500/45 rounded-2xl p-5 flex flex-col items-center gap-3 text-center">
            {svg}
            <p className="text-xs font-semibold text-slate-200">{name}</p>
          </div>
        ))}
      </div>
    </DarkHero>
  );
}

export default function Site() {
  const { pathname } = useLocation();
  useEffect(() => {
    const [t, d] = META[pathname] || META["/"];
    document.title = t;
    const m = document.querySelector('meta[name="description"]');
    if (m) m.setAttribute("content", d);
    window.scrollTo(0, 0);
  }, [pathname]);
  return (
    <div className="min-h-screen bg-slate-50 font-sans" data-testid="marketing-site">
      <SiteHeader />
      <main>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/platform" element={<Platform />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/applications" element={<Applications />} />
          <Route path="/about" element={<About />} />
          <Route path="/request-demo" element={<RequestDemo />} />
          <Route path="/logo-variants" element={<LogoVariants />} />
          <Route path="/privacy" element={<Legal title="Privacy Policy" />} />
          <Route path="/terms" element={<Legal title="Terms of Use" />} />
          <Route path="/cookies" element={<Legal title="Cookie Policy" />} />
          <Route path="*" element={<Home />} />
        </Routes>
      </main>
      <SiteFooter />
    </div>
  );
}
